/**
 * Video/image hero: the play button toggles play/pause (handled by the theme's
 * <video-element>), this only swaps its icon — play triangle while paused,
 * pause bars while playing. Texts always stay visible.
 */
if (!customElements.get('video-image-hero')) {
  const PLAY_ICON =
    '<svg class="vih-icon vih-icon--play" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M25.1398 16.6888L12.8914 10.9952C12.4915 10.773 12 11.0622 12 11.5197V24.4803C12 24.9378 12.4915 25.227 12.8914 25.0048L25.1398 19.3112C26.1685 18.7397 26.1685 17.2603 25.1398 16.6888Z" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  const PAUSE_ICON =
    '<svg class="vih-icon vih-icon--pause" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M13.5 25.5L13.5 10.5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>' +
    '<path d="M22.5 25.5L22.5 10.5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>' +
    '</svg>';

  class VideoImageHero extends HTMLElement {
    connectedCallback() {
      this.initScrollCta();
      // One frame per breakpoint when a separate mobile video is set; each
      // frame owns its video-element, poster and play button.
      this.querySelectorAll('.vih__media-frame').forEach((frame) => this.setupFrame(frame));
    }

    setupFrame(frame) {
      const videoEl = frame.querySelector('video-element');
      if (!videoEl) return;
      this.watchVideoReady(frame, videoEl);

      const playBtn = frame.querySelector('.hero-play-button');
      if (!playBtn) return;
      const iconWrap = playBtn.querySelector('.hero-play-button__icon') || playBtn;
      iconWrap.innerHTML = PLAY_ICON + PAUSE_ICON;

      const sync = () => {
        const playing = videoEl.hasAttribute('playing');
        frame.classList.toggle('is-playing', playing);
        playBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      };
      sync();
      videoEl.addEventListener('video:play', sync);
      videoEl.addEventListener('video:pause', sync);
      // The `playing` attribute is the source of truth; watch it too in case
      // the state changes without an event (e.g. scrolled out of view).
      new MutationObserver(sync).observe(videoEl, { attributes: true, attributeFilter: ['playing'] });
    }

    // CTA "scroll" action: find the target by selector, id or tag name and
    // scroll it into view, keeping a sticky header out of the way.
    initScrollCta() {
      const cta = this.querySelector('[data-scroll-target]');
      if (!cta) return;
      cta.addEventListener('click', (event) => {
        const raw = (cta.dataset.scrollTarget || '').trim();
        if (!raw) return;
        let target = null;
        try {
          target = document.querySelector(raw);
        } catch (e) {
          target = null;
        }
        if (!target) target = document.getElementById(raw.replace(/^#/, ''));
        if (!target) {
          try {
            target = document.querySelector(raw.replace(/^[#.]/, ''));
          } catch (e) {
            target = null;
          }
        }
        if (!target) return;
        event.preventDefault();
        const header = document.querySelector('.header-wrapper, .site-header, header.header');
        const offset = header && getComputedStyle(header).position !== 'static' ? header.offsetHeight : 0;
        const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    }

    // Poster handling: watch for the <video> (inserted from a <template> by
    // the theme) and mark the frame ready once frames are actually rendering.
    watchVideoReady(frame, videoEl) {
      const bind = (video) => {
        if (!video || video.__vihBound) return;
        video.__vihBound = true;
        const ready = () => frame.classList.add('is-video-ready');
        video.addEventListener('playing', ready);
        video.addEventListener('timeupdate', () => {
          if (video.currentTime > 0) ready();
        }, { once: true });
        if (!video.paused && video.readyState >= 3 && video.currentTime > 0) ready();
      };
      bind(videoEl.querySelector('video'));
      new MutationObserver(() => bind(videoEl.querySelector('video'))).observe(videoEl, { childList: true, subtree: true });
    }
  }

  customElements.define('video-image-hero', VideoImageHero);
}
